/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { CQReactTable } from '@laxmimanogna/code-quick-components';
import { UrgentIcon } from '../../../../constants/IconData';

import { createColumnHelper } from '@tanstack/react-table';
import {
  getAuditStatus,
  MY_AUDITS_ORDERING,
} from '../../../../constants/constants';
import useCQMediaQuery from '../../../../hooks/useCQMediaQuery';
import { format } from 'date-fns';

import '../style.css';
import { MyAuditContext } from '../../../../providers/MyAuditProvider';
import { renderActionComponent } from '../MyAuditsComponents';
import {
  createTableProps,
  paginationProps,
} from '../../../../utils/my_audits.utils';
import { useNavigate } from 'react-router-dom';
import { toTitleCase } from '../../../../utils/common.utils';

const columnHelper = createColumnHelper();

const AllMyAuditTable = () => {
  const myAuditContext = useContext(MyAuditContext);
  const navigate = useNavigate();
  const [ordering, setOrdering] = useState('');
  const [highlightedRowId, setHighlightedRowId] = useState(null);

  // media query
  const canScroll = useCQMediaQuery({
    lg: false,
    md: false,
    sm: true,
  });

  const { isUpdating, getAuditUpload, filterParams } = myAuditContext;

  // SCROLL CHANGE: Auto-restore
  const PAGE_NAME = "My Audits";
  const TABLE_NAME = "All";

  useEffect(() => {
      if (myAuditContext.auditUploadList.length > 0) {
        const itemId = getAuditUpload.restoreNavigationState(PAGE_NAME, TABLE_NAME, true);
        if (itemId) {
          setHighlightedRowId(itemId);
          setTimeout(() => {
            setHighlightedRowId(null);
          }, 2000);
        }
      }
    }, [myAuditContext.auditUploadList]);

  useEffect(() => {
    let searching = !!(filterParams.searchText && filterParams.searchText.trim());
    getAuditUpload.loadAPI("My Audit", "All", searching);
  }, [filterParams, getAuditUpload.showOrdering]);

  useEffect(() => {
    if (MY_AUDITS_ORDERING.includes(ordering)) {
      getAuditUpload.setParams({ ordering });
    }
  }, [ordering]);

  const columns = useMemo(
    () => [
      columnHelper.accessor('upload_id', {
        header: '',
        enableSorting: false,
        cell: info => {
          return (
            <HStack
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={10}
            >
              {info.row.original?.urgent ? <UrgentIcon /> : <Box ml={2} />}
            </HStack>
          );
        },
      }),
      columnHelper.accessor('upload_id', {
        header: () => 'Upload #',
        id: 'my-audit-chart_id',
        cell: info => {
          return (
            <HStack
              justifyContent={'flex-start'}
              alignItems={'center'}
              spacing={10}
            >
              {/* {info.row.original?.urgent ? <UrgentIcon /> : <Box ml={2} />} */}
              <Text>{info.renderValue()}</Text>
            </HStack>
          );
        },
      }),
      columnHelper.accessor(row => row.uploaded_date, {
        id: 'upload_date',
        header: () => <span>Upload Date</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'N/A';
          }

          return format(new Date(cellValue), 'MM/dd/yyyy');
        },
      }),
      columnHelper.accessor(row => row.total_page, {
        id: 'total_pages',
        header: () => <span>Pages</span>,
        cell: info => {
          const cellValue = info.getValue();
          if (!cellValue) {
            return 'n/a';
          }

          return cellValue;
        },
      }),
      columnHelper.accessor(p => p.specialties?.name, {
        header: () => 'Specialties',
        id: 'specialty__name',
        cell: info => {
          const specialties = info.row.original.specialties?.name;

          if (!specialties) {
            return 'N/A';
          }
          return specialties === 'ent' ? 'ENT' : toTitleCase(specialties);
        },
      }),

      columnHelper.accessor('status', {
        id: 'status',
        header: () => 'Status',
        cell: info => <Text>{getAuditStatus(info.row.original?.status)}</Text>,
      }),

      columnHelper.accessor(row => row, {
        id: 'actions',
        header: () => 'Actions',
        enableSorting: false,
        cell: info => {
          const obj = info.getValue();
          return renderActionComponent(obj, navigate, myAuditContext, getAuditUpload, PAGE_NAME, TABLE_NAME);
        },
      }),
    ],
    [myAuditContext.isAuditUploadListLoading]
  );

  function handlePreviousPage() {
    myAuditContext.getAuditUpload.loadPrevious("My Audit", "All");
  }

  function handleNextPage() {
    myAuditContext.getAuditUpload.loadNext("My Audit", "All");
  }

  function handleSizeChange(size) {
    myAuditContext.getAuditUpload.loadSize("My Audit", "All", size );
  }

  function handlePageChangeByNumber(pageNumber) {
    myAuditContext.getAuditUpload.loadPageByNumber("My Audit", "All", pageNumber);
  }

  return (
    <Box
      pointerEvents={isUpdating ? 'none' : 'inherit'}
      overflowX={canScroll ? 'scroll' : 'inherit'}
    >
      <CQReactTable
        manualSortBy={true}
        isLoading={myAuditContext.isAuditUploadListLoading}
        data={myAuditContext.auditUploadList}
        columns={columns}
        {...createTableProps(highlightedRowId)}
        {...paginationProps(getAuditUpload)}
        onPreviousPage={handlePreviousPage}
        onNextPage={handleNextPage}
        onSizeChange={handleSizeChange}
        onPageChangeByNumber={handlePageChangeByNumber}
        onAsc={header => {
          setOrdering(header);
        }}
        onDesc={header => {
          setOrdering(`-${header}`);
        }}
      />
    </Box>
  );
};
export default AllMyAuditTable;
